import {
  Box,
  Card,
  CircularProgress,
  Typography,
  useTheme,
  Stack,
  Skeleton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import React, { useEffect, useState } from "react";
import MappedControlDialog from "./AdminDashboardDialogs/MappedControlDialog";
import ReadinessChip from "../../../components/ReadinessChip/ReadinessChip";
const ControlSummary = ({
  data,
  loading,
  heading,
  refetchData,
  cardSx = {},
  contentSx = {},
}) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [dialogChecks, setDialogChecks] = useState([]);

  const handleOpenDialog = (row) => {
    setDialogChecks(row);
    setIsOpen(true);
  };

  const getStatusStyles = (percentage) => {
    if (percentage >= 85) {
      return {
        barColor: "#10b981", // Green
        backgroundColor: "#d1fae5", // Light green
      };
    } else if (percentage >= 60) {
      return {
        barColor: "#3b82f6", // Blue
        backgroundColor: "#dbeafe", // Light blue
      };
    } else if (percentage >= 40) {
      return {
        barColor: "#f59e0b", // Orange/Amber
        backgroundColor: "#fef3c7", // Light orange
      };
    } else {
      return {
        barColor: "#ef4444", // Red
        backgroundColor: "#fee2e2", // Light red
      };
    }
  };

  useEffect(() => {
    if (isOpen && dialogChecks?.area) {
      const updatedRow = data.find((row) => row.area === dialogChecks.area);
      if (updatedRow) {
        setDialogChecks(updatedRow);
      }
    }
  }, [data, isOpen, dialogChecks?.area]);

  const ControlItem = ({ row, index }) => {
    const [isHovered, setIsHovered] = useState(false);
    const readiness = Math.round(row.readinessPercentage || 0);
    const statusStyles = getStatusStyles(readiness);

    return (
      <Box
        key={index}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => handleOpenDialog(row)}
        sx={{
          p: 2.5,
          border: `1px solid ${theme.customColors.border.subtle}`,
          borderRadius: "12px",
          cursor: "pointer",
          backgroundColor: isHovered
            ? theme.customColors.background.hover
            : "#FFFFFF",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          transform: isHovered ? "translateY(-2px)" : "translateY(0)",
          boxShadow: isHovered ? "0 4px 12px -2px rgba(0, 0, 0, 0.08)" : "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1.5,
          }}
        >
          <Box sx={{ flex: 1, pr: 2 }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "15px",
                color: theme.customColors.text.strong,
                mb: 0.5,
                lineHeight: 1.4,
              }}
            >
              {row.area}
            </Typography>
            <Typography
              sx={{
                color: theme.customColors.text.secondary,
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              {row.controls} {row.controls === 1 ? "Control" : "Controls"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ReadinessChip percentage={readiness} />
            <ArrowForwardIcon
              sx={{
                fontSize: "18px",
                color: theme.customColors.text.disabled,
                opacity: isHovered ? 1 : 0,
                transform: isHovered ? "translateX(0)" : "translateX(-4px)",
                transition: "all 0.2s ease",
              }}
            />
          </Box>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ position: "relative" }}>
          {/* Background track */}
          <Box
            sx={{
              height: 6,
              borderRadius: "100px",
              backgroundColor: alpha(statusStyles.barColor, 0.08),
              overflow: "hidden",
              position: "relative",
            }}
          >
            {/* Filled progress */}
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                height: "100%",
                width: `${readiness}%`,
                background: alpha(statusStyles.barColor, 0.5),
                borderRadius: "100px",
                transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: `0 0 8px ${alpha(
                  statusStyles.backgroundColor,
                  0.5
                )}`,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "50%",
                  background: `linear-gradient(180deg, ${alpha(
                    "#FFFFFF",
                    0.3
                  )} 0%, transparent 100%)`,
                  borderRadius: "100px 100px 0 0",
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Card
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          border: `1px solid ${theme.customColors.border.main}`,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
          transition: "box-shadow 0.3s ease",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          },
          ...cardSx,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: "24px 24px 20px",
            borderBottom: `1px solid ${theme.customColors.background.tableRow}`,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "18px",
              color: theme.customColors.text.strong,
              letterSpacing: "-0.02em",
            }}
          >
            {heading}
          </Typography>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            p: "20px 24px 24px",
            overflowY: "auto",
            minHeight: 0,
            ...contentSx,
            // Custom scrollbar styling - hide when loading
            ...(loading
              ? {
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  scrollbarWidth: "none", // Firefox
                  msOverflowStyle: "none", // IE and Edge
                }
              : {
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: alpha(theme.customColors.border.main, 0.7),
                    borderRadius: "10px",
                    "&:hover": {
                      backgroundColor: alpha(
                        theme.customColors.border.main,
                        0.7
                      ),
                    },
                  },
                }),
          }}
        >
          {loading ? (
            <Stack spacing={2}>
              {/* Skeleton Blocks instead of CircularProgress */}
              {[1, 2, 3, 4, 5].map((index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2.5,
                    border: `1px solid ${theme.customColors.border.subtle}`,
                    borderRadius: "12px",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1.5,
                    }}
                  >
                    <Box sx={{ flex: 1, pr: 2 }}>
                      <Skeleton
                        variant="text"
                        width="60%"
                        height={20}
                        sx={{ mb: 0.5 }}
                      />
                      <Skeleton variant="text" width="40%" height={16} />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Skeleton
                        variant="rectangular"
                        width={60}
                        height={24}
                        sx={{ borderRadius: "12px" }}
                      />
                    </Box>
                  </Box>
                  {/* Progress Bar Skeleton */}
                  <Box
                    sx={{
                      position: "relative",
                      height: 6,
                      borderRadius: "100px",
                      backgroundColor: alpha(
                        theme.customColors.border.main,
                        0.08
                      ),
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        height: "100%",
                        width: `${30 + index * 15}%`, // Varying widths: 30%, 45%, 60%, 75%, 90%
                        backgroundColor: alpha(
                          theme.customColors.border.main,
                          0.3
                        ),
                        borderRadius: "100px",
                        animation: "pulse 1.5s ease-in-out infinite",
                        "@keyframes pulse": {
                          "0%, 100%": {
                            opacity: 0.6,
                          },
                          "50%": {
                            opacity: 1,
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : data?.length > 0 ? (
            <Stack spacing={2}>
              {data.map((row, index) => (
                <ControlItem key={index} row={row} index={index} />
              ))}
            </Stack>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 6,
                px: 4,
                backgroundColor: theme.customColors.background.hover,
                borderRadius: "12px",
                border: `1px dashed ${theme.customColors.border.subtle}`,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: theme.customColors.text.primary,
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                No controls found
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors.text.secondary,
                  fontSize: "13px",
                }}
              >
                Controls will appear here once your framework data is loaded
              </Typography>
            </Box>
          )}
        </Box>
      </Card>
      {isOpen && (
        <MappedControlDialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          data={dialogChecks}
          refetchData={refetchData}
        />
      )}
    </>
  );
};

export default ControlSummary;
